import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    AccreditationDocumentDetails,
    LocalTeacherEmploymentSetting,
    LocalTeacherEmploymentType,
    TeacherFields,
    TeacherNotifications,
} from './types'
import {
    ECCAccreditationLevel,
    EmploymentSetting,
} from '../firebase/types-teacher'
import { Dayjs } from 'dayjs'
import { RootState } from './store'
import { AccreditationDocumentConfig } from '../firebase/types-accreditation'

export interface UserUpdate {
    user?: { path?: string; data: any }
    teacher?: { path?: string; data: any }
    // path:{userPath?:string, teacherPath?: string};
    // data:{userData?: any, teacherData?: any};
}

interface IUserRegSlice {
    fields: TeacherFields
    dataLoaded: boolean
    // profiles: {
    //     teacher: TeacherProfileObject | undefined
    //     supportStaff: SupportStaffProfileObject | undefined
    //     educator: EducatorProfileObject | undefined
    // }
    stateTeachingDocuments?: {
        [locationState: string]: AccreditationDocumentDetails[] | null
    } | null
    stateECCDocuments?: {
        [locationState: string]: AccreditationDocumentDetails[] | null
    } | null
    stateSupportStaffDocuments?: {
        [locationState: string]: AccreditationDocumentDetails[] | null
    } | null
    notifications: TeacherNotifications
    step: number
    stateList: string[]
    fromEdit?: string
    openRegModal: boolean
    openProfileModal: boolean
    userUpdate?: UserUpdate
    profilePhotoStep?: boolean
    userDelete?: boolean
    expiredDocsCount: number | null

    // null: No applyDate set
    // Dayjs: applyDate is set, represents when user can apply again
    // false: applyDate is set, user can apply again (been 6 months)
    rejectedState?: null | Dayjs | false

    locationState?: string
    employmentSetting?: LocalTeacherEmploymentSetting
    accreditationModal?: {
        id?: string
        detail: AccreditationDocumentConfig
        setting: EmploymentSetting
    }

    // reviewGroups?: { title: string, step: string, data: any[], state: string, expired?: boolean }[]
}

export const initialState: IUserRegSlice = {
    fields: {},
    dataLoaded: false,
    notifications: {},
    step: 0,
    stateList: [],
    openRegModal: false,
    openProfileModal: false,
    expiredDocsCount: null,
}

export const userRegSlice = createSlice({
    name: 'userReg',
    initialState,
    reducers: {
        setUserRegDefault: () => {
            return initialState
        },
        setRegFields: (state, action: PayloadAction<TeacherFields>) => {
            // Object.assign(state.fields, action.payload)
            state.fields = action.payload
        },
        setStep: (state, action: PayloadAction<number>) => {
            state.step = action.payload
        },
        setStateList: (state, action: PayloadAction<string[]>) => {
            state.stateList = action.payload
        },
        updateFields: (
            state,
            action: PayloadAction<UserUpdate | undefined>
        ) => {
            state.userUpdate = action.payload
        },
        setFromEdit: (state, action: PayloadAction<string | undefined>) => {
            state.fromEdit = action.payload
        },
        setRejectedState: (
            state,
            action: PayloadAction<null | Dayjs | false>
        ) => {
            state.rejectedState = action.payload
        },
        setOpenRegModal: (state, action: PayloadAction<boolean>) => {
            state.openRegModal = action.payload
        },
        setOpenProfileModal: (state, action: PayloadAction<boolean>) => {
            state.openProfileModal = action.payload
        },

        setTeacherNotifications: (
            state,
            action: PayloadAction<TeacherNotifications>
        ) => {
            state.notifications = action.payload
        },
        setProfilePhotoStep: (
            state,
            action: PayloadAction<boolean | undefined>
        ) => {
            state.profilePhotoStep = action.payload
        },
        setUserDelete: (state, action: PayloadAction<boolean>) => {
            state.userDelete = action.payload
        },
        setStateTeachingDocument: (
            state,
            action: PayloadAction<{
                [locationState: string]: AccreditationDocumentDetails[] | null
            } | null>
        ) => {
            state.stateTeachingDocuments = action.payload
        },
        setStateECCTeachingDocument: (
            state,
            action: PayloadAction<{
                [locationState: string]: AccreditationDocumentDetails[] | null
            } | null>
        ) => {
            state.stateECCDocuments = action.payload
        },
        setStateSupportStaffTeachingDocument: (
            state,
            action: PayloadAction<{
                [locationState: string]: AccreditationDocumentDetails[] | null
            } | null>
        ) => {
            state.stateSupportStaffDocuments = action.payload
        },

        setTeacherLocationState: (
            state,
            action: PayloadAction<string | undefined>
        ) => {
            state.locationState = action.payload
        },
        setEmploymentSetting: (
            state,
            action: PayloadAction<
                | {
                      locationState: string
                      type: LocalTeacherEmploymentType
                      level?: ECCAccreditationLevel
                  }
                | undefined
            >
        ) => {
            state.employmentSetting = action.payload
        },

        setExpiredDocsCount: (state, action: PayloadAction<number>) => {
            state.expiredDocsCount = action.payload
        },

        setAccreditationModal: (
            state,
            action: PayloadAction<
                | {
                      id?: string
                      detail: AccreditationDocumentConfig
                      setting: EmploymentSetting
                  }
                | undefined
            >
        ) => {
            state.accreditationModal = action.payload
        },
        setDataLoaded: (state, action: PayloadAction<boolean>) => {
            state.dataLoaded = action.payload
        },
    },
})

export const selectUserDataLoaded = (state: RootState) =>
    state.userReg.dataLoaded
export const selectTeacherType = (state: RootState) =>
    state.userReg.fields.type?.value

export const selectTeacherNotificationSettings = (state: RootState) =>
    state.userReg.notifications

export const {
    setUserRegDefault,
    setRegFields,
    setStep,
    setStateList,
    updateFields,
    setFromEdit,
    setRejectedState,
    setOpenRegModal,
    setOpenProfileModal,
    setTeacherNotifications,
    setProfilePhotoStep,
    setUserDelete,
    setStateTeachingDocument,
    setStateECCTeachingDocument,
    setStateSupportStaffTeachingDocument,

    setTeacherLocationState,
    setEmploymentSetting,
    setExpiredDocsCount,

    setAccreditationModal,

    setDataLoaded,

    // setReviewGroups
} = userRegSlice.actions

export default userRegSlice.reducer
